import {
    Holiday,
    ProjectExtended,
    ProjectSender,
    BarthelScaleQuestion,
    ScaleType,
    ProjectCreationError,
    ProjectSiarRenewal,
    ProjectEndingReason,
    MedicalDevice,
    DisabilityProfileLetter,
    DisabilityProfileIcfCode,
    ParentalResponsibility,
    Applicant,
    NetworkSupport,
    ActivityAutonomy,
    MobilityAutonomy,
    ProjectClinicalPresentation,
    StabilityLevel,
    ProjectClinicalCareNeed,
    ProjectPatientLivingCondition,
    ProjectRehabilitationFacility,
    IadlScaleQuestion,
    AssistiveRegimeEnum,
    ProjectSuspendReason,
    ExportSiarType,
    PrintSiarSection,
    PrintSiarCategorySection,
    ProjectRefType,
    ProjectsColumn,
    ProjectBaseColumn
} from '@/features/projects/types.ts'
import {TKey} from '@/translations/i18n.tsx'
import {palette} from '@/theme/palette.ts'
import {BadgeProps} from '@/components/ui/badge/Badge'
import {differenceInDays, addDays, isSunday} from 'date-fns'
import {PatientExtended} from '@/features/patients/types'
import dayjs from 'dayjs'
import {TeamGroup} from '@/features/treatment-requests/teams/types'
import {AccessFrequency} from '@/features/planned-accesses/types'
import {
    CheckCircleIcon,
    Contrast01Icon,
    Download01Icon,
    IconProps,
    PrinterIcon,
    Share06Icon
} from '@/components/ui/icon/Icon'
import {ForwardRefExoticComponent, RefAttributes} from 'react'
import {HttpGetPlannedAccessesDetailResponse} from '../planned-accesses/http/plannedAccesses.http'

export const DEFAULT_MAIN_PATHOLOGY_VALUES = {
    diagnosisId: '',
    startedAt: '',
    description: '',
    code: ''
}

export const DEFAULT_OTHER_PATHOLOGY_VALUES = {
    diagnosisId: '',
    description: '',
    code: ''
}

export const DEFAULT_MEDICAL_DEVICE_VALUES = {
    id: '',
    device: undefined
}

export const projectStatusToLabel = {
    open: 'commons:open',
    closed: 'commons:closed',
    opening: 'commons:opening'
} as const satisfies Record<NonNullable<ProjectExtended['status']>, TKey>

export const projectStatusToColor = {
    open: palette.light.green['500'],
    closed: palette.light.red['500'],
    opening: palette.light.orange['500']
} as const satisfies Record<NonNullable<ProjectExtended['status']>, string>

export const projectTypeToLabel = {
    continuous: 'commons:continuous',
    cyclic: 'commons:cyclic'
} as const satisfies Record<NonNullable<ProjectExtended['type']>, TKey>

export const projectSiarRenewalToLabel = {
    same_as_previous: 'projects:siar_renewal_same_as_previous',
    empty: 'projects:siar_renewal_empty'
} as const satisfies Record<ProjectSiarRenewal, TKey>

export const projectProgressStatusToLabel = {
    incomplete: 'projects:status_incomplete',
    to_print: 'projects:status_to_print',
    to_export: 'projects:status_to_export',
    to_transmit: 'projects:status_to_transmit',
    transmitted: 'projects:status_transmitted'
} as const satisfies Record<NonNullable<ProjectExtended['progressStatus']>, TKey>

export const projectProgressStatusToIcon = {
    incomplete: Contrast01Icon,
    to_print: PrinterIcon,
    to_export: Download01Icon,
    to_transmit: Share06Icon,
    transmitted: CheckCircleIcon
} as const satisfies Record<
    NonNullable<ProjectExtended['progressStatus']>,
    ForwardRefExoticComponent<IconProps & RefAttributes<SVGSVGElement>>
>

export const projectStatusToBadgeVariant = {
    open: 'success',
    closed: 'danger',
    opening: 'orange'
} as const satisfies Record<NonNullable<ProjectExtended['status']>, BadgeProps['variant']>

export const teamGroupToLabel = {
    referring_doctor: 'projects:referring_doctor',
    therapist: 'commons:therapist',
    almoner: 'commons:almoner',
    psychologist: 'projects:psychologist',
    doctor: 'commons:doctor',
    other: 'commons:other'
} as const satisfies Record<TeamGroup, TKey>

export const scaleTypeToLabel = {
    lapmer: 'projects:lapmer',
    barthel_index: 'projects:barthel_index',
    katz_index: 'projects:katz_index',
    lawton_iadl: 'projects:lawton_iadl',
    mmse: 'projects:mmse',
    vas: 'projects:vas',
    asia_impairment_scale: 'projects:asia_impairment_scale',
    wisci: 'projects:wisci',
    scim: 'projects:scim',
    level_of_cognitive_functioning: 'projects:level_of_cognitive_functioning',
    disability_rating_scale: 'projects:disability_rating_scale',
    six_minutes_walking_test: 'projects:six_minutes_walking_test',
    barthel_index_dyspnea: 'projects:barthel_index_dyspnea',
    glasgow_coma_scale: 'projects:glasgow_coma_scale',
    glasgow_outcome_scale_extended: 'projects:glasgow_outcome_scale_extended',
    modified_rankin_scale: 'projects:modified_rankin_scale',
    rehabilitation_complexity_scale: 'projects:rehabilitation_complexity_scale',
    pedi: 'projects:pedi',
    gmfcs: 'projects:gmfcs',
    gmfm: 'projects:gmfm',
    tinetti: 'projects:tinetti',
    vineland_ii: 'projects:vineland_ii',
    wppsi_iv: 'projects:wppsi_iv',
    wisc_iv: 'projects:wisc_iv',
    raven_matrices: 'projects:raven_matrices',
    tower_of_london: 'projects:tower_of_london',
    movement_abc_2: 'projects:movement_abc_2',
    sensory_disorders_indicator: 'projects:sensory_disorders_indicator',
    motor_disorders_gmfcs: 'projects:motor_disorders_gmfcs',
    motor_disorders_macs: 'projects:motor_disorders_macs',
    motor_disorders_cfcs: 'projects:motor_disorders_cfcs'
} as const satisfies Record<ScaleType, TKey>

const barthelScaleQuestionToLabel = {
    [BarthelScaleQuestion.enum.feeding]: 'projects:barthel_scale_feeding',
    [BarthelScaleQuestion.enum.bathing]: 'projects:barthel_scale_bathing',
    [BarthelScaleQuestion.enum.grooming]: 'projects:barthel_scale_grooming',
    [BarthelScaleQuestion.enum.dressing]: 'projects:barthel_scale_dressing',
    [BarthelScaleQuestion.enum.intenstinal_contincence]: 'projects:barthel_scale_intenstinal_contincence',
    [BarthelScaleQuestion.enum.urinary_contincence]: 'projects:barthel_scale_urinary_contincence',
    [BarthelScaleQuestion.enum.toilet_usage]: 'projects:barthel_scale_toilet_usage',
    [BarthelScaleQuestion.enum.bed_weelchair_transfer]: 'projects:barthel_scale_bed_weelchair_transfer',
    [BarthelScaleQuestion.enum.mobility]: 'projects:barthel_scale_mobility',
    [BarthelScaleQuestion.enum.stairs]: 'projects:barthel_scale_stairs',
    [BarthelScaleQuestion.enum.weelchair_usage]: 'projects:barthel_scale_weelchair_usage'
} as const satisfies Record<BarthelScaleQuestion, TKey>

const iadlScaleQuestionToLabel = {
    [IadlScaleQuestion.enum.phone_usage]: 'projects:iadl_phone_usage',
    [IadlScaleQuestion.enum.shopping]: 'projects:iadl_shopping',
    [IadlScaleQuestion.enum.cooking]: 'projects:iadl_cooking',
    [IadlScaleQuestion.enum.housekeeping]: 'projects:iadl_housekeeping',
    [IadlScaleQuestion.enum.laundry]: 'projects:iadl_laundry',
    [IadlScaleQuestion.enum.transportation]: 'projects:iadl_transportation',
    [IadlScaleQuestion.enum.medication]: 'projects:iadl_medication',
    [IadlScaleQuestion.enum.finance]: 'projects:iadl_finance'
} as const satisfies Record<IadlScaleQuestion, TKey>

export const iadlScaleQuestions = [
    {
        title: iadlScaleQuestionToLabel[IadlScaleQuestion.enum.phone_usage],
        name: IadlScaleQuestion.enum.phone_usage,
        options: [
            {label: 'projects:iadl_phone_usage_1', value: '1', score: 1},
            {label: 'projects:iadl_phone_usage_2', value: '2', score: 1},
            {label: 'projects:iadl_phone_usage_3', value: '3', score: 1},
            {label: 'projects:iadl_phone_usage_4', value: '4', score: 0}
        ]
    },
    {
        title: iadlScaleQuestionToLabel[IadlScaleQuestion.enum.shopping],
        name: IadlScaleQuestion.enum.shopping,
        options: [
            {label: 'projects:iadl_shopping_1', value: '1', score: 1},
            {label: 'projects:iadl_shopping_2', value: '2', score: 0},
            {label: 'projects:iadl_shopping_3', value: '3', score: 0},
            {label: 'projects:iadl_shopping_4', value: '4', score: 0}
        ]
    },
    {
        title: iadlScaleQuestionToLabel[IadlScaleQuestion.enum.cooking],
        name: IadlScaleQuestion.enum.cooking,
        options: [
            {label: 'projects:iadl_cooking_1', value: '1', score: 1},
            {label: 'projects:iadl_cooking_2', value: '2', score: 0},
            {label: 'projects:iadl_cooking_3', value: '3', score: 0},
            {label: 'projects:iadl_cooking_4', value: '4', score: 0}
        ]
    },
    {
        title: iadlScaleQuestionToLabel[IadlScaleQuestion.enum.housekeeping],
        name: IadlScaleQuestion.enum.housekeeping,
        options: [
            {label: 'projects:iadl_housekeeping_1', value: '1', score: 1},
            {label: 'projects:iadl_housekeeping_2', value: '2', score: 1},
            {label: 'projects:iadl_housekeeping_3', value: '3', score: 0},
            {label: 'projects:iadl_housekeeping_4', value: '4', score: 0}
        ]
    },
    {
        title: iadlScaleQuestionToLabel[IadlScaleQuestion.enum.laundry],
        name: IadlScaleQuestion.enum.laundry,
        options: [
            {label: 'projects:iadl_laundry_1', value: '1', score: 1},
            {label: 'projects:iadl_laundry_2', value: '2', score: 1},
            {label: 'projects:iadl_laundry_3', value: '3', score: 0}
        ]
    },
    {
        title: iadlScaleQuestionToLabel[IadlScaleQuestion.enum.transportation],
        name: IadlScaleQuestion.enum.transportation,
        options: [
            {label: 'projects:iadl_transportation_1', value: '1', score: 1},
            {label: 'projects:iadl_transportation_2', value: '2', score: 1},
            {label: 'projects:iadl_transportation_3', value: '3', score: 1},
            {label: 'projects:iadl_transportation_4', value: '4', score: 0},
            {label: 'projects:iadl_transportation_5', value: '5', score: 0}
        ]
    },
    {
        title: iadlScaleQuestionToLabel[IadlScaleQuestion.enum.medication],
        name: IadlScaleQuestion.enum.medication,
        options: [
            {label: 'projects:iadl_medication_1', value: '1', score: 1},
            {label: 'projects:iadl_medication_2', value: '2', score: 0},
            {label: 'projects:iadl_medication_3', value: '3', score: 0}
        ]
    },
    {
        title: iadlScaleQuestionToLabel[IadlScaleQuestion.enum.finance],
        name: IadlScaleQuestion.enum.finance,
        options: [
            {label: 'projects:iadl_finance_1', value: '1', score: 1},
            {label: 'projects:iadl_finance_2', value: '2', score: 1},
            {label: 'projects:iadl_finance_3', value: '3', score: 0}
        ]
    }
] as const satisfies Array<{
    title: TKey
    name: IadlScaleQuestion
    options: Array<{
        label: TKey
        value: `${number}`
        score: number
    }>
}>

export const barthelScaleMainQuestions = [
    {
        title: barthelScaleQuestionToLabel[BarthelScaleQuestion.enum.feeding],
        name: BarthelScaleQuestion.enum.feeding,
        options: [
            {label: 'projects:barthel_scale_feeding_10', value: '10'},
            {label: 'projects:barthel_scale_feeding_8', value: '8'},
            {label: 'projects:barthel_scale_feeding_5', value: '5'},
            {label: 'projects:barthel_scale_feeding_2', value: '2'},
            {label: 'projects:barthel_scale_feeding_0', value: '0'}
        ]
    },
    {
        title: barthelScaleQuestionToLabel[BarthelScaleQuestion.enum.bathing],
        name: BarthelScaleQuestion.enum.bathing,
        options: [
            {label: 'projects:barthel_scale_bathing_5', value: '5'},
            {label: 'projects:barthel_scale_bathing_4', value: '4'},
            {label: 'projects:barthel_scale_bathing_3', value: '3'},
            {label: 'projects:barthel_scale_bathing_1', value: '1'},
            {label: 'projects:barthel_scale_bathing_0', value: '0'}
        ]
    },
    {
        title: barthelScaleQuestionToLabel[BarthelScaleQuestion.enum.grooming],
        name: BarthelScaleQuestion.enum.grooming,
        options: [
            {label: 'projects:barthel_scale_grooming_5', value: '5'},
            {label: 'projects:barthel_scale_grooming_4', value: '4'},
            {label: 'projects:barthel_scale_grooming_3', value: '3'},
            {label: 'projects:barthel_scale_grooming_1', value: '1'},
            {label: 'projects:barthel_scale_grooming_0', value: '0'}
        ]
    },
    {
        title: barthelScaleQuestionToLabel[BarthelScaleQuestion.enum.dressing],
        name: BarthelScaleQuestion.enum.dressing,
        options: [
            {label: 'projects:barthel_scale_dressing_10', value: '10'},
            {label: 'projects:barthel_scale_dressing_8', value: '8'},
            {label: 'projects:barthel_scale_dressing_5', value: '5'},
            {label: 'projects:barthel_scale_dressing_2', value: '2'},
            {label: 'projects:barthel_scale_dressing_0', value: '0'}
        ]
    },
    {
        title: barthelScaleQuestionToLabel[BarthelScaleQuestion.enum.intenstinal_contincence],
        name: BarthelScaleQuestion.enum.intenstinal_contincence,
        options: [
            {label: 'projects:barthel_scale_intenstinal_contincence_10', value: '10'},
            {label: 'projects:barthel_scale_intenstinal_contincence_8', value: '8'},
            {label: 'projects:barthel_scale_intenstinal_contincence_5', value: '5'},
            {label: 'projects:barthel_scale_intenstinal_contincence_2', value: '2'},
            {label: 'projects:barthel_scale_intenstinal_contincence_0', value: '0'}
        ]
    },
    {
        title: barthelScaleQuestionToLabel[BarthelScaleQuestion.enum.urinary_contincence],
        name: BarthelScaleQuestion.enum.urinary_contincence,
        options: [
            {label: 'projects:barthel_scale_urinary_contincence_10', value: '10'},
            {label: 'projects:barthel_scale_urinary_contincence_8', value: '8'},
            {label: 'projects:barthel_scale_urinary_contincence_5', value: '5'},
            {label: 'projects:barthel_scale_urinary_contincence_2', value: '2'},
            {label: 'projects:barthel_scale_urinary_contincence_0', value: '0'}
        ]
    },
    {
        title: barthelScaleQuestionToLabel[BarthelScaleQuestion.enum.toilet_usage],
        name: BarthelScaleQuestion.enum.toilet_usage,
        options: [
            {label: 'projects:barthel_scale_toilet_usage_10', value: '10'},
            {label: 'projects:barthel_scale_toilet_usage_8', value: '8'},
            {label: 'projects:barthel_scale_toilet_usage_5', value: '5'},
            {label: 'projects:barthel_scale_toilet_usage_2', value: '2'},
            {label: 'projects:barthel_scale_toilet_usage_0', value: '0'}
        ]
    },
    {
        title: barthelScaleQuestionToLabel[BarthelScaleQuestion.enum.bed_weelchair_transfer],
        name: BarthelScaleQuestion.enum.bed_weelchair_transfer,
        options: [
            {label: 'projects:barthel_scale_bed_weelchair_transfer_15', value: '15'},
            {label: 'projects:barthel_scale_bed_weelchair_transfer_12', value: '12'},
            {label: 'projects:barthel_scale_bed_weelchair_transfer_8', value: '8'},
            {label: 'projects:barthel_scale_bed_weelchair_transfer_3', value: '3'},
            {label: 'projects:barthel_scale_bed_weelchair_transfer_0', value: '0'}
        ]
    },
    {
        title: barthelScaleQuestionToLabel[BarthelScaleQuestion.enum.mobility],
        name: BarthelScaleQuestion.enum.mobility,
        options: [
            {label: 'projects:barthel_scale_mobility_15', value: '15'},
            {label: 'projects:barthel_scale_mobility_12', value: '12'},
            {label: 'projects:barthel_scale_mobility_8', value: '8'},
            {label: 'projects:barthel_scale_mobility_3', value: '3'},
            {label: 'projects:barthel_scale_mobility_0', value: '0'}
        ]
    },
    {
        title: barthelScaleQuestionToLabel[BarthelScaleQuestion.enum.stairs],
        name: BarthelScaleQuestion.enum.stairs,
        options: [
            {label: 'projects:barthel_scale_stairs_10', value: '10'},
            {label: 'projects:barthel_scale_stairs_8', value: '8'},
            {label: 'projects:barthel_scale_stairs_5', value: '5'},
            {label: 'projects:barthel_scale_stairs_2', value: '2'},
            {label: 'projects:barthel_scale_stairs_0', value: '0'}
        ]
    },
    {
        title: barthelScaleQuestionToLabel[BarthelScaleQuestion.enum.weelchair_usage],
        name: BarthelScaleQuestion.enum.weelchair_usage,
        options: [
            {label: 'projects:barthel_scale_weelchair_usage_5', value: '5'},
            {label: 'projects:barthel_scale_weelchair_usage_4', value: '4'},
            {label: 'projects:barthel_scale_weelchair_usage_3', value: '3'},
            {label: 'projects:barthel_scale_weelchair_usage_1', value: '1'},
            {label: 'projects:barthel_scale_weelchair_usage_0', value: '0'}
        ]
    }
] as const satisfies Array<{
    title: TKey
    name: BarthelScaleQuestion
    options: Array<{
        label: TKey
        value: `${number}`
    }>
}>

export const projectCycleCountToLabel = {
    one_cycle: 'projects:cycle_count_one_cycle',
    two_cycles: 'projects:cycle_count_two_cicles',
    single_non_repeatable_cycle: 'projects:cycle_count_single_cycle'
} as const satisfies Record<NonNullable<ProjectExtended['numberOfCycles']>, TKey>

export const projectAssistiveRegimeToLabel = {
    autism: 'projects:care_sub_regime_autism_intellectual_disabilities',
    adults: 'projects:care_sub_regime_adults_other_diagnoses',
    minors: 'projects:care_sub_regime_minors_other_diagnoses'
} as const satisfies Record<NonNullable<ProjectExtended['assistiveRegime']>, TKey>

export const projectSenderToLabel = {
    district_services_specialist: 'projects:sender_district_services_specialist',
    hospital_specialist: 'projects:sender_hospital_specialist',
    pediatrician_doctor: 'projects:sender_pediatrician_doctor'
} as const satisfies Record<ProjectSender, TKey>

export const projectSectorToLabel = {
    outpatient: 'commons:outpatient',
    domiciliary: 'commons:domiciliary',
    mixed: 'commons:mixed',
    extended: 'projects:legacy_sector_extended',
    maintenance: 'projects:legacy_sector_maintenance'
} as const satisfies Record<NonNullable<ProjectExtended['sector']>, TKey>

export const projectEndingReasonToLabel = {
    full_recovery: 'projects:ending_reason_outcome_full_recovery',
    home_care: 'projects:ending_reason_outcome_home_care',
    remote_treatment: 'projects:ending_reason_outcome_remote_treatment',
    outpatient_treatment: 'projects:ending_reason_outcome_outpatient_treatment',
    semi_residential_facility: 'projects:ending_reason_outcome_semi_residential_facility',
    residential_facility: 'projects:ending_reason_outcome_residential_facility',
    hospital_facility: 'projects:ending_reason_outcome_hospital_facility',
    deceased: 'projects:ending_reason_outcome_deceased',
    proactive_health_programs: 'projects:ending_reason_outcome_proactive_health_programs',
    residence_change: 'projects:ending_reason_outcome_residence_change',
    abandonment: 'projects:ending_reason_outcome_abandonment',
    other: 'commons:other',
    administrative_closure: 'projects:ending_reason_outcome_administrative_closure'
} as const satisfies Record<ProjectEndingReason, TKey>

export const projectMedicalDeviceToLabel = {
    personal_care_and_protection_devices: 'projects:medical_device_personal_care_and_protection_devices',
    trunk_orthoses: 'projects:medical_device_trunk_orthoses',
    postural_systems: 'projects:medical_device_postural_systems',
    limb_devices_and_orthoses: 'projects:medical_device_limb_devices_and_orthoses',
    optical_and_acoustic_devices_and_prostheses: 'projects:medical_device_optical_and_acoustic_devices_and_prostheses',
    respiratory_function_devices: 'projects:medical_device_respiratory_function_devices',
    walking_aids: 'projects:medical_device_walking_aids',
    abdominal_aids: 'projects:medical_device_abdominal_aids',
    communication_aids: 'projects:medical_device_communication_aids',
    other: 'commons:other'
} as const satisfies Record<NonNullable<MedicalDevice['device']>, TKey>

export const parentalResponsibilityToLabel = {
    legal_guardian: 'projects:parental_responsibility_legal_guardian',
    healthcare_facility: 'projects:parental_responsibility_healthcare_facility',
    missing_data: 'projects:parental_responsibility_missing_data',
    shared: 'projects:parental_responsibility_shared',
    maternal: 'projects:parental_responsibility_maternal',
    paternal: 'projects:parental_responsibility_paternal'
} as const satisfies Record<ParentalResponsibility, TKey>

export const applicantToLabel = {
    hospital_acute_care_ward: 'projects:applicant_hospital_acute_care_ward',
    hospital_rehabilitation_department: 'projects:applicant_hospital_rehabilitation_department',
    structure: 'projects:applicant_structure',
    regional_services: 'projects:applicant_regional_services',
    territorial_operations_center: 'projects:applicant_territorial_operations_center',
    ambulatory_specialist: 'projects:applicant_ambulatory_specialist',
    other: 'projects:applicant_other_structure'
} as const satisfies Record<Applicant, TKey>

export const networkSupportToLabel = {
    presence: 'projects:network_support_presence',
    partial: 'projects:network_support_partial',
    non_presence: 'projects:network_support_non_presence'
} as const satisfies Record<NetworkSupport, TKey>

export const activityAutonomyToLabel = {
    autonomous: 'projects:activity_autonomy_autonomous',
    partially_dependent: 'projects:activity_autonomy_partially_dependent',
    totally_dependent: 'projects:activity_autonomy_totally_dependent'
} as const satisfies Record<ActivityAutonomy, TKey>

export const mobilityAutonomyToLabel = {
    moves_by_himself_protect_env: 'projects:mobility_autonomy_moves_by_himself_protect_env',
    moves_by_himself_not_protect_env: 'projects:mobility_autonomy_moves_by_himself_not_protect_env',
    moves_with_assistance_protect_env: 'projects:mobility_autonomy_moves_with_assistance_protect_env',
    moves_with_assistance_not_protect_env: 'projects:mobility_autonomy_moves_with_assistance_not_protect_env',
    does_not_move: 'projects:mobility_autonomy_does_not_move'
} as const satisfies Record<MobilityAutonomy, TKey>

export const clinicalPresentationToLabel = {
    mild_nonexistent: 'projects:clinical_presentation_mild_nonexistent',
    moderate: 'projects:clinical_presentation_moderate',
    severe: 'projects:clinical_presentation_severe'
} as const satisfies Record<ProjectClinicalPresentation, TKey>

export const stabilityLevelToLabel = {
    evaluation_not_performed: 'projects:clinical_stability_not_performed',
    stable: 'projects:clinical_stability_stable',
    moderately_stable: 'projects:clinical_stability_moderately_stable',
    moderately_unstable: 'projects:clinical_stability_moderately_unstable',
    unstable: 'projects:clinical_stability_unstable',
    highly_unstable: 'projects:clinical_stability_highly_unstable'
} as const satisfies Record<StabilityLevel, TKey>

export const careNeedToLabel = {
    sng_peg: 'projects:nursing_care_needs_sng_peg',
    tracheotomy: 'projects:nursing_care_needs_tracheostomy',
    assisted_ventilation: 'projects:nursing_care_needs_ventilator',
    oxygen_therapy: 'projects:nursing_care_needs_oxygen_therapy',
    dialysis: 'projects:nursing_care_needs_dialysis',
    pressure_ulcers: 'projects:nursing_care_needs_pressure_ulcers'
} as const satisfies Record<ProjectClinicalCareNeed, TKey>

export const livingConditionsToLabel = {
    alone_or_with_partner: 'projects:patient_living_condition_alone_or_with_partner',
    with_partner_and_children: 'projects:patient_living_condition_with_partner_and_children',
    with_other_family: 'projects:patient_living_condition_with_other_family',
    with_parents: 'projects:patient_living_condition_with_parents',
    in_community_facility: 'projects:patient_living_condition_in_community_facility',
    alone_with_personal_assistant: 'projects:patient_living_condition_alone_with_personal_assistant',
    alone: 'projects:patient_living_condition_alone'
} as const satisfies Record<ProjectPatientLivingCondition, TKey>

export const rehabilitationFacilitiesMapToLabel = {
    none: 'commons:none',
    hospital_facility: 'projects:rehabilitation_facilities_hospital_facility',
    extra_hospital_facility: 'projects:rehabilitation_facilities_extra_hospital_facility',
    current_facility: 'projects:rehabilitation_facilities_current_facility',
    missing_info: 'projects:rehabilitation_facilities_missing_info'
} as const satisfies Record<ProjectRehabilitationFacility, TKey>

// This is a list of Italian holidays. It is not complete and should be expanded by the backend
const getItalianHolidays = (year: number): Holiday[] => [
    {date: new Date(year, 0, 1), name: 'Capodanno'},
    {date: new Date(year, 0, 6), name: 'Epifania'},
    {date: new Date(year, 3, 25), name: 'Festa della Liberazione'},
    {date: new Date(year, 4, 1), name: 'Festa dei Lavoratori'},
    {date: new Date(year, 5, 2), name: 'Festa della Repubblica'},
    {date: new Date(year, 7, 15), name: 'Ferragosto'},
    {date: new Date(year, 10, 1), name: 'Tutti i Santi'},
    {date: new Date(year, 11, 8), name: 'Immacolata Concezione'},
    {date: new Date(year, 11, 25), name: 'Natale'},
    {date: new Date(year, 11, 26), name: 'Santo Stefano'}
]

/**
 * Maps the project column enum to the label
 * @param column - The project column enum
 * @returns The label
 */
export const columnNameToLabel = {
    [ProjectsColumn.enum.fullName]: 'commons:patient',
    [ProjectsColumn.enum.sector]: 'commons:workSector',
    [ProjectsColumn.enum.birthDate]: 'commons:birth_date_label',
    [ProjectsColumn.enum.serviceType]: 'commons:service',
    [ProjectsColumn.enum.type]: 'projects:project_type',
    [ProjectsColumn.enum.code]: 'commons:pri_n',
    [ProjectsColumn.enum.startDate]: 'projects:start_date',
    [ProjectsColumn.enum.durationDays]: 'commons:duration',
    [ProjectsColumn.enum.expirationDate]: 'commons:expiration',
    [ProjectsColumn.enum.status]: 'commons:status',
    [ProjectsColumn.enum.isRenewed]: 'projects:renewed',
    [ProjectsColumn.enum.progressStatus]: 'projects:progress',
    [ProjectsColumn.enum.endDate]: 'projects:end_date_column',
    [ProjectsColumn.enum.completedAccessesPerCent]: 'projects:completed_accesses',
    [ProjectsColumn.enum.absenceDays]: 'projects:absences',
    [ProjectsColumn.enum.suspensionDays]: 'projects:suspension_days',
    [ProjectsColumn.enum.openingExportedAt]: 'projects:opening_exported_at',
    [ProjectsColumn.enum.openingTransmissionAt]: 'projects:opening_transmission_at',
    [ProjectsColumn.enum.closingExportedAt]: 'projects:closing_exported_at',
    [ProjectsColumn.enum.closingTransmissionAt]: 'projects:closing_transmission_at',
    [ProjectsColumn.enum.referringDoctorId]: 'projects:referring_doctor',
    [ProjectsColumn.enum.telephone]: 'commons:telephone',
    [ProjectsColumn.enum.taxCode]: 'commons:fiscal_code',
    [ProjectsColumn.enum.isSuspended]: 'commons:suspended'
} as const satisfies Record<ProjectBaseColumn, TKey>
/**
 * Determines if a date is a holiday in Italy
 */
const isHoliday = (date: Date, holidays: Holiday[]): boolean => {
    return holidays.some(
        holiday => holiday.date.getDate() == date.getDate() && holiday.date.getMonth() === date.getMonth()
    )
}

/**
 * Calculates working days between two dates (excluding weekends and Italian holidays)
 * @param startDate - Starting date
 * @param endDate - Ending date
 * @param holidayDates - Optional array of holiday dates from the API
 * @returns Number of working days
 */
export const calculateWorkingDays = (startDate: Date, endDate: Date, holidayDates?: Date[]): number => {
    if (!startDate || !endDate || startDate > endDate) {
        return 0
    }

    const totalDays = differenceInDays(endDate, startDate)
    let workingDays = 0
    const currentDate = new Date(startDate)

    // If holidayDates is provided, use them
    if (holidayDates && holidayDates.length > 0) {
        for (let i = 0; i < totalDays; i++) {
            const date = addDays(currentDate, i)
            const isDateHoliday = holidayDates.some(
                holiday =>
                    holiday.getDate() == date.getDate() &&
                    holiday.getMonth() == date.getMonth() &&
                    holiday.getFullYear() == date.getFullYear()
            )

            if (!isSunday(date) && !isDateHoliday) {
                workingDays++
            }
        }
    } else {
        // Fallback to built-in holiday list
        // Get holidays for the relevant years
        const startYear = startDate.getFullYear()
        const endYear = endDate.getFullYear()

        const years = Array.from({length: endYear - startYear + 1}, (_, i) => startYear + i)

        const holidays = years.flatMap(getItalianHolidays)

        for (let i = 0; i < totalDays; i++) {
            const date = addDays(currentDate, i)

            if (!isSunday(date) && !isHoliday(date, holidays)) {
                workingDays++
            }
        }
    }

    return workingDays
}

/**
 * Checks if a patient is eligible for project creation
 * @param patient - The patient to check
 * @param setError - The function to set the error
 * @returns true if the patient is eligible, false otherwise
 */
export const checkProjectCreationEligibility = (
    patient: PatientExtended,
    setError?: (error: ProjectCreationError | null) => void
) => {
    if (patient.project && patient.project?.status !== 'closed') {
        setError?.('already_exists')
        return false
    }

    if (patient?.status == 'deceased') {
        setError?.('deceased')
        return false
    }

    if (!patient?.treatmentRequest) {
        setError?.('tratment_request_error')
        return false
    }

    setError?.(null)
    return true
}

/**
 * Calculates the next working day (all days except weekends) after a given date, excluding holidays
 * @param date - The date to calculate the next working day from
 * @returns The next working day
 */
export const getNextWorkingDay = (date: Date, holidays?: {date: string}[]): Date => {
    let nextDate = dayjs(date).add(1, 'day')
    const maxIterations = 10 // Safety limit to prevent infinite loops

    // Skip weekends
    for (let i = 0; i < maxIterations && (nextDate.day() === 0 || nextDate.day() === 6); i++) {
        nextDate = nextDate.add(1, 'day')
    }

    // Skip holidays if provided
    if (holidays) {
        for (
            let i = 0;
            i < maxIterations && holidays.some(holiday => holiday.date === nextDate.format('YYYY-MM-DD'));
            i++
        ) {
            nextDate = nextDate.add(1, 'day')
        }
    }

    return nextDate.toDate()
}

/**
 * Ensures the provided date is a working day. If it's a weekend or holiday, returns the next working day
 * @param date - The date to check
 * @param holidays - Optional array of holidays to exclude
 * @returns The same date if it's a working day, or the next working day
 */
export const ensureWorkingDay = (date: Date, holidays?: {date: string}[]): Date => {
    let currentDate = dayjs(date)
    const maxIterations = 10 // Safety limit to prevent infinite loops

    // Check if current date is weekend or holiday and skip to next working day
    for (
        let i = 0;
        i < maxIterations &&
        (currentDate.day() === 0 ||
            currentDate.day() === 6 ||
            (holidays && holidays.some(holiday => holiday.date === currentDate.format('YYYY-MM-DD'))));
        i++
    ) {
        currentDate = currentDate.add(1, 'day')
    }

    return currentDate.toDate()
}

export const disabiltyProfileLetterToTitle = {
    [DisabilityProfileLetter.enum.b]: 'projects:disability_profile_letter_b_title',
    [DisabilityProfileLetter.enum.s]: 'projects:disability_profile_letter_s_title',
    [DisabilityProfileLetter.enum.d]: 'projects:disability_profile_letter_d_title',
    [DisabilityProfileLetter.enum.e]: 'projects:disability_profile_letter_e_title'
} as const satisfies Record<DisabilityProfileLetter, TKey>

export const disabiltyProfileLetterToIcfCodes = (
    letter: DisabilityProfileLetter
): Array<{
    code: DisabilityProfileIcfCode
    label: TKey
}> =>
    DisabilityProfileIcfCode.options
        .filter(code => code.startsWith(letter))
        .map(code => ({
            code,
            label: `projects:disability_profile_${code}`
        }))

export const disabiltyProfileLetterToQualifiers = {
    [DisabilityProfileLetter.enum.b]: [
        {
            label: 'projects:disability_profile_qualifier_default_title',
            options: [
                {value: 0, label: 'projects:disability_profile_qualifier_default_0'},
                {value: 1, label: 'projects:disability_profile_qualifier_default_1'},
                {value: 2, label: 'projects:disability_profile_qualifier_default_2'},
                {value: 3, label: 'projects:disability_profile_qualifier_default_3'},
                {value: 4, label: 'projects:disability_profile_qualifier_default_4'},
                {value: 8, label: 'projects:disability_profile_qualifier_default_8'},
                {value: 9, label: 'projects:disability_profile_qualifier_default_9'}
            ]
        }
    ],
    [DisabilityProfileLetter.enum.s]: [
        {
            label: 'projects:disability_profile_qualifier_default_title',
            options: [
                {value: 0, label: 'projects:disability_profile_qualifier_default_0'},
                {value: 1, label: 'projects:disability_profile_qualifier_default_1'},
                {value: 2, label: 'projects:disability_profile_qualifier_default_2'},
                {value: 3, label: 'projects:disability_profile_qualifier_default_3'},
                {value: 4, label: 'projects:disability_profile_qualifier_default_4'},
                {value: 8, label: 'projects:disability_profile_qualifier_default_8'},
                {value: 9, label: 'projects:disability_profile_qualifier_default_9'}
            ]
        },
        {
            label: 'projects:disability_profile_s_qualifier2_title',
            options: [
                {value: 0, label: 'projects:disability_profile_s_qualifier2_0'},
                {value: 1, label: 'projects:disability_profile_s_qualifier2_1'},
                {value: 2, label: 'projects:disability_profile_s_qualifier2_2'},
                {value: 3, label: 'projects:disability_profile_s_qualifier2_3'},
                {value: 4, label: 'projects:disability_profile_s_qualifier2_4'},
                {value: 5, label: 'projects:disability_profile_s_qualifier2_5'},
                {value: 6, label: 'projects:disability_profile_s_qualifier2_6'},
                {value: 7, label: 'projects:disability_profile_s_qualifier2_7'},
                {value: 8, label: 'projects:disability_profile_s_qualifier2_8'},
                {value: 9, label: 'projects:disability_profile_s_qualifier2_9'}
            ]
        }
    ],
    [DisabilityProfileLetter.enum.d]: [
        {
            label: 'projects:disability_profile_d_qualifier1_title',
            options: [
                {value: 0, label: 'projects:disability_profile_qualifier_default_0'},
                {value: 1, label: 'projects:disability_profile_qualifier_default_1'},
                {value: 2, label: 'projects:disability_profile_qualifier_default_2'},
                {value: 3, label: 'projects:disability_profile_qualifier_default_3'},
                {value: 4, label: 'projects:disability_profile_qualifier_default_4'},
                {value: 8, label: 'projects:disability_profile_qualifier_default_8'},
                {value: 9, label: 'projects:disability_profile_qualifier_default_9'}
            ]
        },
        {
            label: 'projects:disability_profile_d_qualifier2_title',
            options: [
                {value: 0, label: 'projects:disability_profile_qualifier_default_0'},
                {value: 1, label: 'projects:disability_profile_qualifier_default_1'},
                {value: 2, label: 'projects:disability_profile_qualifier_default_2'},
                {value: 3, label: 'projects:disability_profile_qualifier_default_3'},
                {value: 4, label: 'projects:disability_profile_qualifier_default_4'},
                {value: 8, label: 'projects:disability_profile_qualifier_default_8'},
                {value: 9, label: 'projects:disability_profile_qualifier_default_9'}
            ]
        }
    ]
} as const satisfies Record<
    Exclude<DisabilityProfileLetter, 'e'>,
    Array<{label: TKey; options: Array<{value: number; label: TKey}>}>
>

export const disabiltyProfileLetterEToQualifiers = {
    type: {
        label: 'projects:disability_profile_e_qualifier1_title',
        options: [
            {value: 'barrier', label: 'projects:disability_profile_e_qualifier1_0'},
            {value: 'helper', label: 'projects:disability_profile_e_qualifier1_1'}
        ]
    },
    qualifier: {
        barrier: {
            label: 'projects:disability_profile_e_qualifier1_0',
            options: [
                {value: 0, label: 'projects:disability_profile_e_qualifier2_1_0'},
                {value: -1, label: 'projects:disability_profile_e_qualifier2_1_1'},
                {value: -2, label: 'projects:disability_profile_e_qualifier2_1_2'},
                {value: -3, label: 'projects:disability_profile_e_qualifier2_1_3'},
                {value: -4, label: 'projects:disability_profile_e_qualifier2_1_4'},
                {value: -8, label: 'projects:disability_profile_e_qualifier2_1_8'},
                {value: -9, label: 'projects:disability_profile_e_qualifier2_1_9'}
            ]
        },
        helper: {
            label: 'projects:disability_profile_e_qualifier1_1',
            options: [
                {value: 0, label: 'projects:disability_profile_e_qualifier2_2_0'},
                {value: 1, label: 'projects:disability_profile_e_qualifier2_2_1'},
                {value: 2, label: 'projects:disability_profile_e_qualifier2_2_2'},
                {value: 3, label: 'projects:disability_profile_e_qualifier2_2_3'},
                {value: 4, label: 'projects:disability_profile_e_qualifier2_2_4'},
                {value: 8, label: 'projects:disability_profile_e_qualifier2_2_8'},
                {value: 9, label: 'projects:disability_profile_e_qualifier2_2_9'}
            ]
        }
    }
} as const satisfies {
    type: {label: TKey; options: Array<{value: 'barrier' | 'helper'; label: TKey}>}
    qualifier: {
        barrier: {label: TKey; options: Array<{value: number; label: TKey}>}
        helper: {label: TKey; options: Array<{value: number; label: TKey}>}
    }
}

export const projectRefTypeMapToLabel = {
    renewed: 'projects:renewal',
    transformed: 'projects:transform',
    varied: 'projects:reopen'
} as const satisfies Record<ProjectRefType, TKey>

export const calculateTotalAccesses = (duration: number, frequency: AccessFrequency, numberOfAccesses: number) => {
    if (!duration || !frequency || !numberOfAccesses || numberOfAccesses <= 0) return 0
    return (frequency == 'weekly' ? Math.round(duration / 7) : Math.round(duration / 30)) * numberOfAccesses
}

/**
 * Calculates numberOfAccesses based on totalAccesses, duration, and frequency
 * Inverse operation of calculateTotalAccesses
 *
 * @param duration - Duration in days
 * @param frequency - Frequency of access (weekly or monthly)
 * @param totalAccesses - Total number of accesses
 * @returns The number of accesses per week/month needed to achieve the total
 */
export const calculateNumberOfAccesses = (
    duration: number,
    frequency: AccessFrequency,
    totalAccesses: number
): number => {
    if (!duration || !frequency || !totalAccesses || totalAccesses <= 0) return 0

    const periodCount = frequency === 'weekly' ? Math.round(duration / 7) : Math.round(duration / 30)

    if (periodCount <= 0) return 0

    // Round to nearest integer to handle potential division issues
    return Math.round(totalAccesses / periodCount)
}

export const calculateTotalPlannedAccesses = (
    duration: number,
    plannedAccesses: HttpGetPlannedAccessesDetailResponse
) => {
    const treatmentsSum = plannedAccesses.treatments.reduce(
        (sum, item) => sum + calculateTotalAccesses(duration, item.frequency ?? 'weekly', item.numberOfAccesses),
        0
    )

    const inPersonSum = plannedAccesses.in_person_accesses.reduce((sum, item) => sum + item.numberOfAccesses, 0)

    const nonInPersonSum = plannedAccesses.non_in_person_accesses.reduce((sum, item) => sum + item.numberOfAccesses, 0)

    return treatmentsSum + inPersonSum + nonInPersonSum
}

export const calculateTotalAccessInAbsence = (plannedAccesses: HttpGetPlannedAccessesDetailResponse) => {
    return plannedAccesses.non_in_person_accesses.reduce((sum, item) => sum + item.numberOfAccesses, 0)
}

export const frequencyAccessesToLabel = {
    [AccessFrequency.enum.weekly]: 'projects:total_accesses_per_week',
    [AccessFrequency.enum.monthly]: 'projects:total_accesses_per_month'
} as const satisfies Record<AccessFrequency, TKey>

export const assistiveRegimeToRehabCareCommitment = {
    adults: 50,
    autism: 60,
    minors: 60
} as const satisfies Record<AssistiveRegimeEnum, 50 | 60>

export const suspendReasonToLabel = {
    [ProjectSuspendReason.enum.patient_medical_reasons]: 'projects:suspend_reason_patient_medical_reasons',
    [ProjectSuspendReason.enum.patient_hospital_reasons]: 'projects:suspend_reason_patient_hospital_reasons',
    [ProjectSuspendReason.enum.patient_personal_reasons]: 'projects:suspend_reason_patient_personal_reasons',
    [ProjectSuspendReason.enum.therapist_health_reasons]: 'projects:suspend_reason_therapist_health_reasons'
} as const satisfies Record<ProjectSuspendReason, TKey>

export const exportSiarTypeToLabel = {
    open_project: 'commons:project_opening',
    close_project: 'commons:project_closing'
} as const satisfies Record<ExportSiarType, TKey>

export const siarSections = {
    open_project: {
        title: 'projects:siar_sections_initial',
        sections: [
            {
                title: 'projects:siar_section_a',
                description: 'projects:siar_section_a_description',
                value: 'A'
            },
            {
                title: 'projects:siar_section_b',
                description: 'projects:siar_section_b_description',
                value: 'B'
            },
            {
                title: 'projects:siar_section_c',
                description: 'projects:siar_section_c_description',
                value: 'C'
            },
            {
                title: 'projects:siar_section_d',
                description: 'projects:siar_section_d_description',
                value: 'D'
            }
        ]
    },
    close_project: {
        title: 'projects:siar_sections_final',
        sections: [
            {
                title: 'projects:siar_section_e',
                description: 'projects:siar_section_e_description',
                value: 'E'
            },
            {
                title: 'projects:siar_section_f',
                description: 'projects:siar_section_f_description',
                value: 'F'
            }
        ]
    }
} as const satisfies Record<
    PrintSiarCategorySection,
    {title: TKey; sections: {title: TKey; description: TKey; value: PrintSiarSection}[]}
>

/**
 * Determines the progress bar variant based on the percentage
 * @param percentage - The percentage value to evaluate
 * @returns The variant string: 'danger', 'primary', or 'neutral'
 */
export const getProgressBarVariant = (percentage: number): 'danger' | 'primary' | 'neutral' => {
    if (percentage >= 100) return 'danger'
    if (percentage > 0) return 'primary'
    return 'neutral'
}

/**
 * Returns the maximum absence percentage based on the project type
 * @param project - The project object
 * @returns The maximum absence percentage
 */
export const getMaxAssencePercentageByProjectType = (project: ProjectExtended) => {
    if (project.patient.serviceType === 'adults') return 20
    if (project.patient.serviceType === 'evolutionary_age') return 40
    return 0
}
